import { Beer } from './types';

let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

function authHeaders(): Record<string, string> {
  if (authToken) {
    return { Authorization: `Basic ${authToken}` };
  }
  return {};
}

export async function fetchBeers(): Promise<Beer[]> {
  const res = await fetch('/api/beers');
  if (!res.ok) throw new Error('Failed to fetch beers');
  return res.json();
}

export async function createBeer(data: FormData): Promise<Beer> {
  const res = await fetch('/api/beers', {
    method: 'POST',
    headers: authHeaders(),
    body: data,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to create beer');
  }
  return res.json();
}

export async function updateBeer(id: string, data: FormData): Promise<Beer> {
  const res = await fetch(`/api/beers/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: data,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to update the drink');
  }
  return res.json();
}

export async function deleteBeer(id: string): Promise<void> {
  const res = await fetch(`/api/beers/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to delete beer');
  }
}

export async function login(username: string, password: string): Promise<string> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (res.status === 429) {
    throw new Error('Too many attempts. Wait a minute and try again.');
  }
  if (!res.ok) {
    throw new Error('Invalid credentials');
  }
  const data = await res.json();
  return data.token;
}

export async function removeBackground(image: Blob): Promise<Blob> {
  const data = new FormData();
  data.append('image', image, 'upload.png');
  const res = await fetch('/api/admin/remove-bg', {
    method: 'POST',
    headers: authHeaders(),
    body: data,
  });
  if (res.status === 429) {
    throw new Error('Rate limit reached. Wait a minute before trying again.');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to remove the background');
  }
  const json = await res.json();
  return await (await fetch(json.image as string)).blob(); // data URI -> Blob
}

export function imageUrl(imageId: string): string {
  return `/api/images/${imageId}`;
}

const TRANSLIT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

// Beer page id: transliterated, lowercased, non-alphanumerics collapsed to
// dashes. "Каскадер IPA 2" -> "kaskader-ipa-2". Derived from the name rather
// than stored, so it stays readable in a QR code on the bottle.
export function beerSlug(name: string): string {
  return [...name.toLowerCase()]
    .map((c) => TRANSLIT[c] ?? c)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function beerPageUrl(name: string): string {
  return `${location.origin}/brews/${beerSlug(name)}`;
}

export async function generateLabelImage(description: string): Promise<string> {
  const res = await fetch('/api/admin/label-image', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ description }),
  });
  if (res.status === 429) {
    throw new Error('Rate limit reached. Wait a minute before generating again.');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to generate image');
  }
  const data = await res.json();
  return data.image as string; // data URI
}
